import { gql, useQuery } from '@apollo/client'
import { Box, Skeleton } from '@mui/material'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import React, { useContext, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { activePromotionsForCustomer, myLoyaltyPoints } from '../../apollo/server'
import { Header, LoginHeader } from '../../components/Header'
import DealCard, { dealGroup } from '../../components/Deals/DealCard'
import UserContext from '../../context/User'
import ConfigurationContext from '../../context/Configuration'
import { useLocationContext } from '../../context/Location'
import { useOrderMode, ORDER_MODES } from '../../context/OrderMode'
import Seo from '../../components/Seo/Seo'
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS, TYPE, topBarOffset } from '../../utils/designTokens'

const GET_DEALS = gql`${activePromotionsForCustomer}`
const MY_LOYALTY = gql`${myLoyaltyPoints}`

// Restaurants includes a restaurant's catering deals; Catering is only those.
const TABS = [
  { key: 'ALL', label: 'All offers', test: () => true },
  { key: 'PLATFORM', label: 'RushMyFood', test: (p) => !p.restaurantId },
  { key: 'RESTAURANT', label: 'Restaurants', test: (p) => Boolean(p.restaurantId) },
  { key: 'CATERING', label: 'Catering', test: (p) => Boolean(p.isCatering) },
]

function RewardsCard({ points }) {
  return (
    <Box
      component={RouterLink}
      to="/rewards"
      aria-label={`My Rewards: ${points.toLocaleString()} points`}
      sx={{
        ...FOCUS_RING,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 240,
        p: '14px 16px',
        borderRadius: '16px',
        bgcolor: C.ink,
        color: '#FFFFFF',
        textDecoration: 'none',
        transition: 'transform .15s ease',
        '&:hover': { transform: 'translateY(-1px)' },
      }}
    >
      <Box aria-hidden="true" sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: '12px', bgcolor: 'rgba(245,183,49,0.18)', color: C.goldAccent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <StarRoundedIcon sx={{ fontSize: 26 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: C.goldAccent }}>MY REWARDS</Box>
        <Box sx={{ fontFamily: FONT_BODY, fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{points.toLocaleString()} pts</Box>
      </Box>
      <ChevronRightRoundedIcon aria-hidden="true" sx={{ color: 'rgba(255,255,255,0.7)' }} />
    </Box>
  )
}

function Deals() {
  const navigate = useNavigate()
  const { isLoggedIn, cart, restaurant: cartRestaurant } = useContext(UserContext)
  const configuration = useContext(ConfigurationContext)
  const currency = configuration?.currencySymbol || '$'
  const [tab, setTab] = useState('ALL')
  const tabRefs = useRef({})
  const { location } = useLocationContext()
  const { orderMode } = useOrderMode()

  // Restaurant offers only from restaurants in this address's results, for
  // the Delivery/Pickup mode the customer has picked (the sidebar's badge
  // counts with the same variables).
  const { data, loading } = useQuery(GET_DEALS, {
    variables: {
      latitude: location?.latitude || null,
      longitude: location?.longitude || null,
      pickup: orderMode === ORDER_MODES.PICKUP,
    },
    fetchPolicy: 'cache-and-network',
  })
  const { data: loyaltyData } = useQuery(MY_LOYALTY, { skip: !isLoggedIn, fetchPolicy: 'cache-first' })
  const points = loyaltyData?.myLoyaltyPoints?.balance

  const promos = useMemo(() => data?.activePromotionsForCustomer || [], [data])
  const counts = useMemo(() => {
    const out = {}
    TABS.forEach((tb) => { out[tb.key] = promos.filter(tb.test).length })
    return out
  }, [promos])
  const shown = useMemo(() => promos.filter(TABS.find((t) => t.key === tab).test), [promos, tab])

  // The open cart's subtotal, once the restaurant page has priced every line
  // (it writes each line's price into the cart). Unknown → no cart box.
  const cartSubtotal = useMemo(() => {
    if (!cartRestaurant || !cart?.length) return null
    if (!cart.every((c) => Number.isFinite(parseFloat(c.price)))) return null
    return cart.reduce((s, c) => s + parseFloat(c.price) * (parseInt(c.quantity, 10) || 0), 0)
  }, [cart, cartRestaurant])
  // A restaurant's deal counts the cart only when the cart is from there; a
  // RushMyFood deal counts any cart. Catering carts can't be told apart here.
  const subtotalFor = (p) => {
    if (cartSubtotal == null || p.isCatering) return null
    if (!p.restaurantId) return cartSubtotal
    return String(p.restaurantId) === String(cartRestaurant) ? cartSubtotal : null
  }

  const onAction = (p) => {
    const group = dealGroup(p)
    if (group === 'platform' || !p.restaurantSlug) navigate('/restaurant-list')
    else navigate(`/restaurant/${p.restaurantSlug}`, { state: { id: p.restaurantId, name: p.restaurantName, slug: p.restaurantSlug, ...(group === 'catering' ? { catering: true } : {}) } })
  }

  const onTabKey = (e) => {
    const i = TABS.findIndex((t) => t.key === tab)
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : null
    if (next == null) return
    e.preventDefault()
    const t = TABS[(next + TABS.length) % TABS.length]
    setTab(t.key)
    tabRefs.current[t.key]?.focus()
  }

  const today = new Date()
  const weekday = today.toLocaleDateString('en-US', { weekday: 'long' })
  const date = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  const firstLoad = loading && !data

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.bg }}>
      <Seo
        title="Food Deals & Restaurant Offers"
        description="Today's deals and promotions from local restaurants on RushMyFood. Find discounts, free delivery offers and limited-time menu specials near you."
        path="/deals"
      />
      {isLoggedIn ? <Header /> : <LoginHeader />}

      <Box sx={{ boxSizing: 'border-box', width: '100%', px: { xs: 2, sm: 3, md: 4 }, pt: { xs: topBarOffset(20), md: topBarOffset(28) }, pb: 6 }}>
        <Box component="header" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box component="h1" sx={{ ...TYPE.pageTitle, m: 0, color: C.ink }}>Deals</Box>
            <Box component="p" sx={{ m: 0, mt: 0.75, fontFamily: FONT_BODY, fontSize: 15, color: C.muted }}>
              {weekday}, {date} ·{' '}
              {firstLoad ? 'finding offers' : (
                <>
                  <Box component="strong" sx={{ color: C.ink, fontWeight: 700 }}>{promos.length}</Box>
                  {` active ${promos.length === 1 ? 'offer' : 'offers'} you can use right now`}
                </>
              )}
            </Box>
          </Box>
          {isLoggedIn && typeof points === 'number' && <RewardsCard points={points} />}
        </Box>

        <Box
          role="tablist"
          aria-label="Deals"
          onKeyDown={onTabKey}
          sx={{ mt: 3.5, pb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}` }}
        >
          {TABS.map((t) => {
            const selected = tab === t.key
            return (
              <Box
                key={t.key}
                component="button"
                type="button"
                role="tab"
                id={`deals-tab-${t.key}`}
                aria-selected={selected}
                aria-controls="deals-panel"
                tabIndex={selected ? 0 : -1}
                ref={(el) => { tabRefs.current[t.key] = el }}
                onClick={() => setTab(t.key)}
                sx={{
                  ...BUTTON_RESET,
                  ...FOCUS_RING,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  height: 44,
                  px: 2,
                  boxSizing: 'border-box',
                  borderRadius: RADIUS.pill,
                  fontFamily: FONT_BODY,
                  fontSize: 14,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  bgcolor: selected ? C.ink : C.bg,
                  color: selected ? '#FFFFFF' : C.ink,
                  border: selected ? `1.5px solid ${C.ink}` : `1.5px solid ${C.borderStrong}`,
                  '&:hover': { bgcolor: selected ? C.ink : C.surface },
                }}
              >
                {t.label}
                <Box component="span" sx={{ minWidth: 22, height: 22, px: 0.75, boxSizing: 'border-box', borderRadius: RADIUS.pill, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, bgcolor: selected ? 'rgba(255,255,255,0.2)' : C.surface2, color: selected ? '#FFFFFF' : C.ink }}>
                  {firstLoad ? '·' : counts[t.key]}
                </Box>
              </Box>
            )
          })}
        </Box>

        <Box id="deals-panel" role="tabpanel" aria-labelledby={`deals-tab-${tab}`} sx={{ mt: 3 }}>
          {firstLoad ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '20px' }} aria-busy="true">
              {[0, 1, 2].map((i) => (
                <Box key={i}>
                  <Skeleton variant="rectangular" height={150} sx={{ borderRadius: '22px' }} />
                  <Skeleton width="70%" height={28} />
                  <Skeleton width="45%" />
                </Box>
              ))}
            </Box>
          ) : shown.length === 0 ? (
            <Box sx={{ border: `1.5px dashed ${C.borderStrong}`, borderRadius: '22px', px: 3, py: 6, textAlign: 'center' }}>
              <Box component="p" sx={{ m: 0, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 700, color: C.ink }}>No deals here right now</Box>
              <Box component="p" sx={{ ...TYPE.body, m: 0, mt: 0.75, color: C.muted }}>Check back soon, new offers drop all the time.</Box>
            </Box>
          ) : (
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: '20px' }}>
              {shown.map((p) => (
                <Box component="li" key={p._id} sx={{ minWidth: 0, display: 'flex', '& > article': { flex: 1 } }}>
                  <DealCard promo={p} currency={currency} cartSubtotal={subtotalFor(p)} onAction={onAction} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default Deals
